/**
 * Mirrors backend OrderType / OrderStatus from The_Platform.Entity.Enums
 * (JsonStringEnumConverter serializes enum names as strings).
 */
export type OrderTypeName =
  | 'DirectCoursePurchase'
  | 'BuyCoinsDirect'
  | 'WalletChargeByCode'
  | 'WalletCoinsUsage'
  | 'AdminCoinsGrant'
  | 'AdminEnrollment';

export type OrderStatusName = 'Pending' | 'Completed' | 'Cancelled' | 'Refunded';

export const ORDER_TYPE_ID: Record<OrderTypeName, number> = {
  DirectCoursePurchase: 0,
  BuyCoinsDirect: 1,
  WalletChargeByCode: 2,
  WalletCoinsUsage: 3,
  AdminCoinsGrant: 4,
  AdminEnrollment: 5,
};

const ORDER_TYPE_BY_ID: Record<number, OrderTypeName> = {
  0: 'DirectCoursePurchase',
  1: 'BuyCoinsDirect',
  2: 'WalletChargeByCode',
  3: 'WalletCoinsUsage',
  4: 'AdminCoinsGrant',
  5: 'AdminEnrollment',
};

export const ORDER_TYPE_FILTER_OPTIONS: { value: OrderTypeName; label: string }[] = [
  { value: 'DirectCoursePurchase', label: 'شراء كورس / محاضرة مباشر' },
  { value: 'BuyCoinsDirect', label: 'شراء عملات مباشر' },
  { value: 'WalletChargeByCode', label: 'شحن المحفظة بالكود' },
  { value: 'WalletCoinsUsage', label: 'استخدام عملات المحفظة' },
  { value: 'AdminCoinsGrant', label: 'منح عملات من الإدارة' },
  { value: 'AdminEnrollment', label: 'تسجيل من الإدارة' },
];

export const ORDER_STATUS_FILTER_OPTIONS: { value: OrderStatusName; label: string }[] = [
  { value: 'Pending', label: 'قيد الانتظار' },
  { value: 'Completed', label: 'مكتمل' },
  { value: 'Cancelled', label: 'ملغي' },
  { value: 'Refunded', label: 'مسترد' },
];

export interface OrderTypeContext {
  type?: string | number | null;
  typeId?: number | null;
  courseId?: string | number | null;
  unitId?: string | number | null;
  courseName?: string | null;
  unitName?: string | null;
}

function hasValue(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

export function resolveOrderTypeName(order: OrderTypeContext): OrderTypeName | null {
  if (typeof order.type === 'string' && order.type in ORDER_TYPE_ID) {
    return order.type as OrderTypeName;
  }

  if (typeof order.type === 'number' && ORDER_TYPE_BY_ID[order.type]) {
    return ORDER_TYPE_BY_ID[order.type];
  }

  if (typeof order.typeId === 'number' && ORDER_TYPE_BY_ID[order.typeId]) {
    return ORDER_TYPE_BY_ID[order.typeId];
  }

  return null;
}

export function resolveOrderTypeId(order: OrderTypeContext): number | null {
  const name = resolveOrderTypeName(order);
  return name !== null ? ORDER_TYPE_ID[name] : null;
}

export function getOrderTypeLabel(order: OrderTypeContext): string {
  const typeName = resolveOrderTypeName(order);
  const isUnit = hasValue(order.unitId) && !hasValue(order.courseId);
  const isCourse = hasValue(order.courseId);

  switch (typeName) {
    case 'DirectCoursePurchase':
      if (isUnit) return 'شراء محاضرة مباشر';
      if (isCourse) return 'شراء كورس مباشر';
      return 'شراء مباشر';
    case 'BuyCoinsDirect':
      return 'شراء عملات مباشر';
    case 'WalletChargeByCode':
      return 'شحن المحفظة بالكود';
    case 'WalletCoinsUsage':
      if (isUnit) return 'شراء محاضرة بالعملات';
      if (isCourse) return 'شراء كورس بالعملات';
      return 'استخدام عملات المحفظة';
    case 'AdminCoinsGrant':
      return 'منح عملات من الإدارة';
    case 'AdminEnrollment':
      if (isUnit) return 'تسجيل محاضرة من الإدارة';
      if (isCourse) return 'تسجيل كورس من الإدارة';
      return 'تسجيل من الإدارة';
    default:
      return 'غير محدد';
  }
}

export function getOrderTypeBadgeClass(order: OrderTypeContext): string {
  const typeName = resolveOrderTypeName(order);

  switch (typeName) {
    case 'DirectCoursePurchase':
      return 'bg-blue-100 text-blue-800';
    case 'BuyCoinsDirect':
      return 'bg-amber-100 text-amber-800';
    case 'WalletChargeByCode':
      return 'bg-teal-100 text-teal-800';
    case 'WalletCoinsUsage':
      return 'bg-violet-100 text-violet-800';
    case 'AdminCoinsGrant':
      return 'bg-fuchsia-100 text-fuchsia-800';
    case 'AdminEnrollment':
      return 'bg-indigo-100 text-indigo-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export function getOrderProductLabel(order: OrderTypeContext): string {
  const typeName = resolveOrderTypeName(order);

  if (order.courseName?.trim()) return order.courseName.trim();
  if (order.unitName?.trim()) return order.unitName.trim();

  switch (typeName) {
    case 'BuyCoinsDirect':
    case 'WalletChargeByCode':
    case 'AdminCoinsGrant':
      return 'رصيد / عملات';
    default:
      return 'غير محدد';
  }
}

export function getOrderStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'Completed':
      return 'مكتمل';
    case 'Pending':
      return 'قيد الانتظار';
    case 'Cancelled':
      return 'ملغي';
    case 'Refunded':
      return 'مسترد';
    default:
      return status || 'غير محدد';
  }
}

export function getOrderStatusBadgeClass(status: string | null | undefined): string {
  switch (status) {
    case 'Completed':
      return 'bg-green-100 text-green-800';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'Cancelled':
      return 'bg-red-100 text-red-800';
    case 'Refunded':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}
